import "./list-payment.scss";

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Sidebar } from "primereact/sidebar";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";

import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import SearchInput from "@shared/ui/search-input/search-input";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useQyGetPayment } from "@factory/query/payment.query";
import {
  GetPaymentModel,
  GetResponsePaymentModel,
} from "@factory/model/query-model-payment";
import { TableSort } from "@shared/models/table-sort";
import { FilterPayment } from "../filter-payment/filter-payment";
import { useFilterPaymentContext } from "../filter-payment/filter-payment.context";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import { isEmpty } from "lodash-es";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function ListPayment() {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const { contractId } = useParams();
  const [responseData, setResponseData] = useState<GetPaymentModel[]>([]);
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterPaymentContext();
  const { formatDateLocal } = useDateTimeContext();

  // THIS IS THE QUERY DECLARATION
  const handleApiPaymentSuccess = async (response: GetResponsePaymentModel) => {
    const _responseData = response.data || [];
    if (_responseData.length === 0) {
      return;
    }

    const updatedResponse = await Promise.all(
      _responseData.map(async (element) => {
        if (element.avatar) {
          try {
            const elementFile = await viewAsset(element.avatar);
            const fileBlob = URL.createObjectURL(elementFile);
            return { ...element, fileBlob };
          } catch (error) {
            console.error("Error fetching signature data:", error);
            return { ...element, fileBlob: null };
          }
        } else {
          return { ...element, fileBlob: null };
        }
      })
    );
    setResponseData(updatedResponse);
  };
  const {
    data: paymentResponse,
    isLoading,
    isError,
    error,
  } = useQyGetPayment(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues(),
    !!contractId,
    handleApiPaymentSuccess
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetPaymentModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterPayment />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{paymentResponse?.count}</b>
      </h5>

      <DataTable
        value={responseData}
        selectionMode="single"
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column
          body={(cell) => currencyFormat(cell.amount_paid, "PHP")}
          field="amount_paid"
          header="Amount Paid"
          sortable
        ></Column>
        <Column
          field="payment_date"
          header="Date Paid"
          body={(cell) => {
            if (isEmpty(cell.payment_date)) {
              return "-";
            }
            return formatDateLocal(cell.payment_date);
          }}
        ></Column>
        <Column field="billing_status" header="Status" sortable></Column>
        <Column field="provider_name" header="Provider" sortable></Column>
        <Column
          field="photo"
          header="Attachment"
          body={(rowData) => {
            if (rowData.fileBlob) {
              return (
                <img
                  src={rowData.fileBlob}
                  alt={`attachment`}
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              );
            }

            return <div>-</div>;
          }}
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={paymentResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListPayment" className="payment">
      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListPayment;
